import { useNavigate, useParams } from 'react-router-dom';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Intake from 'components/intake/Intake';
import { FormMeta, FormValues, PatientFormMode } from 'components/intake/types';
import { FormHelpers } from 'context/form/types';
import { useProfile } from 'context/profile';
import ImportedPatient from 'models/ImportedPatient';
import { importedPatientsQueryKeys, showImportedPatient } from 'services/api/importedPatients';
import { createPatient } from 'services/api/patient';
import { portfolioQueryKeys } from 'services/api/portfolio';
import { coordinateRequestQueryKeys } from 'services/api/reviews';
import { useCoordinatePatientsActions } from 'stores/patientFiltersStore';
import { useToastActions } from 'stores/toastStore';

export default function CoordinatePatientPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { addToast } = useToastActions();
  const { id } = useParams<{ id?: string }>();
  const { profile } = useProfile();
  const { reset } = useCoordinatePatientsActions();

  const { data: importedPatient } = useQuery({
    queryKey: ['importedPatient', id],
    queryFn: () =>
      showImportedPatient({
        id: id ?? '',
        include:
          'owner.group_type,transferred_from,physician_team,episode_classification,plan_type_classification,attr_values.attr,pending_coordinate_request.provider_group.group_type',
      }),
    enabled: !!id,
  });

  const { mutate: createNewPatient } = useMutation({
    mutationFn: createPatient,
  });

  const handleSubmit = async (values: Partial<FormValues>, _meta, helpers: FormHelpers<FormValues, FormMeta>) => {
    helpers.setMeta('isSubmitting', true);

    const patientValues = ImportedPatient.fromFormValues(values).serialize();

    createNewPatient(patientValues, {
      onSuccess: (patient) => {
        queryClient.invalidateQueries({ queryKey: portfolioQueryKeys.lane('queue') });
        queryClient.invalidateQueries({ queryKey: coordinateRequestQueryKeys.base });
        queryClient.invalidateQueries({ queryKey: importedPatientsQueryKeys.index });
        addToast({ text: 'Patient successfully added to portfolio.' });
        reset();
        navigate(`/patients/${patient.episodeId}`);
      },
      onError: () => {
        addToast({ text: 'Something went wrong. Please try again.' });
      },
      onSettled: () => {
        helpers.setMeta('isSubmitting', false);
      },
    });
  };

  if (!importedPatient) return <></>;

  const initialValues = importedPatient?.intakeFormValues();

  return (
    <Intake
      initialMeta={{ isSubmitting: false, mode: PatientFormMode.Connecting, isManagerUser: profile.canActAsManager }}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      patient={importedPatient}
    />
  );
}
